import { useEffect, useMemo, useRef, useState } from 'react';
import { BulkOperationsWorkspace, type BulkOperationsWorkspaceProps } from '../../bulk/public';
import {
  AddOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Stack,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import type {
  StaffingChangeSummary,
  StaffingDraft,
  StaffingHireReference,
  StaffingReference,
  StaffingWorker,
} from '../operations/model';
import type { StaffingAccess, StaffingNavigation, StaffingOperationsPort } from '../operations/port';
import { fixtureStaffingWorkers } from '../operations/fixtures';
import {
  fixtureJobLevels,
  fixtureJobs,
  fixtureJobTypes,
  fixtureLocations,
  fixtureOrganizations,
  fixtureWorkSpaces,
  initialFixtureChanges,
} from '../fixtures/staffingPageFixtures';
import {
  addDateOnlyDays,
  clearWorkSpaceValue,
  errorCode,
  errorStatus,
  formatDateOnly,
  formatInstantDate,
  lifecycleAfterLabel,
  matchesStatus,
  processLabel,
  subjectLabel,
  timeTypeLabels,
  type ChangeType,
  type DraftReference,
  type StaffingRow,
  type StatusFilter,
} from '../model/staffingPagePresentation';
import { StaffingChangeComposerDialog } from '../components/StaffingChangeComposerDialog';
import { StaffingChangeDetailDrawer } from '../components/StaffingChangeDetailDrawer';
import { StaffingChangesTable } from '../components/StaffingChangesTable';
import { StaffingCorrectionDialog } from '../components/StaffingCorrectionDialog';
import { WorkerPersonnelRequestActions, isManagedPersonnelRequest } from '../components/WorkerPersonnelRequestActions';
import { StaffingPlansWorkspace } from '../components/StaffingPlansWorkspace';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { useCollectionDetailRoute } from '../../../shared/useCollectionDetailRoute';
import type { CollectionDetailRoutePort } from '../../../shared/collectionDetailRoute';
import {
  DetailDrawer,
  DetailDrawerBody,
  FeedbackSnackbar,
  FilterTabs,
  ResourceState,
  ResourcePage,
  type ResourceCollectionState,
} from '../../../design-system';

export type StaffingPageProps = {
  port: StaffingOperationsPort;
  navigation: StaffingNavigation;
  detailRoute: CollectionDetailRoutePort;
  planDetailRoute: CollectionDetailRoutePort;
  access: StaffingAccess;
  fixtureMode: boolean;
  bulkWorkspace?: Omit<BulkOperationsWorkspaceProps, 'children' | 'onCompleted'>;
};

export function StaffingPage(props: StaffingPageProps) {
  const [bulkRevision, setBulkRevision] = useState(0);
  if (!props.bulkWorkspace) return <StaffingPageContent {...props} />;
  return <BulkOperationsWorkspace {...props.bulkWorkspace} onCompleted={() => setBulkRevision((value) => value + 1)}>
    {(bulk) => <StaffingPageContent {...props} bulkRevision={bulkRevision} navigation={{ ...props.navigation,
      openBulkChange: () => bulk.openCreate('STAFFING_CHANGE'),
      openBulkHistory: props.bulkWorkspace?.canRead ? bulk.openHistory : undefined,
    }} />}
  </BulkOperationsWorkspace>;
}

function StaffingPageContent({ port, navigation, detailRoute, planDetailRoute, access, fixtureMode, bulkRevision = 0 }: StaffingPageProps & { bulkRevision?: number }) {
  const { selectedId: selectedChangeID, openDetail, closeDetail } = useCollectionDetailRoute(detailRoute);
  const { selectedId: selectedPlanID } = useCollectionDetailRoute(planDetailRoute);
  const [view, setView] = useState<'plans' | 'history'>(() => selectedChangeID ? 'history' : 'plans');
  const [composePlanRequest, setComposePlanRequest] = useState(0);
  useEffect(() => { if (selectedChangeID) setView('history'); }, [selectedChangeID]);
  useEffect(() => { if (selectedPlanID) setView('plans'); }, [selectedPlanID]);
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const tomorrow = addDateOnlyDays(today, 1);
  const compactTable = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { canMutate, canBulkCreate, canOpenWorkflowRequests } = access;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [personnelBusy, setPersonnelBusy] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [workerSearchQuery, setWorkerSearchQuery] = useState('');
  const [fixtureChanges, setFixtureChanges] = useState(initialFixtureChanges);
  useEffect(() => {
    const timer = window.setTimeout(() => setWorkerSearchQuery(workerSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [workerSearch]);
  const composerWorkersResource = useAsyncResource(
    () => !dialogOpen || !effectiveDate ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureStaffingWorkers) : port.listWorkers(workerSearchQuery, effectiveDate),
    [port, dialogOpen, effectiveDate, fixtureMode, workerSearchQuery],
  );
  const organizationsResource = useAsyncResource(
    () => !dialogOpen || !effectiveDate ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureOrganizations) : port.listOrganizations(effectiveDate),
    [port, dialogOpen, effectiveDate, fixtureMode],
  );
  const locationsResource = useAsyncResource(
    () => !dialogOpen || !effectiveDate ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureLocations) : port.listLocations(effectiveDate),
    [port, dialogOpen, effectiveDate, fixtureMode],
  );
  const jobsResource = useAsyncResource(
    () => !dialogOpen || !effectiveDate ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureJobs) : port.listJobProfiles(effectiveDate),
    [port, dialogOpen, effectiveDate, fixtureMode],
  );
  const jobLevelsResource = useAsyncResource(
    () => !dialogOpen || !effectiveDate ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureJobLevels) : port.listJobLevels(effectiveDate),
    [port, dialogOpen, effectiveDate, fixtureMode],
  );
  const history = useAsyncResource(
    () => fixtureMode ? Promise.resolve({ total: fixtureChanges.length, data: fixtureChanges }) : port.listChanges(),
    [port, fixtureMode, fixtureChanges, bulkRevision],
  );
  const draftHistory = useAsyncResource(
    () => fixtureMode ? Promise.resolve({ total: fixtureChanges.filter((change) => change.status === 'DRAFT').length, data: fixtureChanges.filter((change) => change.status === 'DRAFT') }) : port.listChanges('', 'DRAFT'),
    [port, fixtureMode, fixtureChanges, bulkRevision],
  );
  const approvalHistory = useAsyncResource(
    () => fixtureMode ? Promise.resolve({ total: fixtureChanges.filter((change) => change.status === 'IN_PROGRESS').length, data: fixtureChanges.filter((change) => change.status === 'IN_PROGRESS') }) : port.listChanges('', 'IN_PROGRESS'),
    [port, fixtureMode, fixtureChanges, bulkRevision],
  );
  const scheduledHistory = useAsyncResource(
    () => fixtureMode ? Promise.resolve({ total: fixtureChanges.filter((change) => change.status === 'COMPLETED' && Boolean(change.effectiveDate && change.effectiveDate >= tomorrow)).length, data: fixtureChanges.filter((change) => change.status === 'COMPLETED' && Boolean(change.effectiveDate && change.effectiveDate >= tomorrow)) }) : port.listChanges('', 'COMPLETED', tomorrow),
    [port, fixtureMode, fixtureChanges, tomorrow, bulkRevision],
  );
  const completedHistory = useAsyncResource(
    () => fixtureMode ? Promise.resolve({ total: fixtureChanges.filter((change) => change.status === 'COMPLETED').length, data: fixtureChanges.filter((change) => change.status === 'COMPLETED') }) : port.listChanges('', 'COMPLETED'),
    [port, fixtureMode, fixtureChanges, bulkRevision],
  );

  const [query, setQuery] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [notice, setNotice] = useState('');
  const [noticeWorkflow, setNoticeWorkflow] = useState<StaffingReference>();
  const [selectedChange, setSelectedChange] = useState<StaffingChangeSummary>();
  const [detailActionStates, setDetailActionStates] = useState<Record<string, { loading: boolean; error?: string }>>({});
  const detailActionState = selectedChangeID ? detailActionStates[selectedChangeID] : undefined;
  const detailActionLoading = detailActionState?.loading ?? false;
  const detailActionError = detailActionState?.error;
  const detailSubmitKeys = useRef(new Map<string, string>());

  const [dialogStep, setDialogStep] = useState(0);
  const [changeType, setChangeType] = useState<ChangeType>('JOB_CHANGE');
  const [selectedWorker, setSelectedWorker] = useState<StaffingWorker>();
  const [organizationID, setOrganizationID] = useState('');
  const [jobProfileID, setJobProfileID] = useState('');
  const [jobLevelID, setJobLevelID] = useState('');
  const [jobTypeID, setJobTypeID] = useState('');
  const [locationID, setLocationID] = useState('');
  const [workSpaceSelection, setWorkSpaceSelection] = useState('');
  const [timeType, setTimeType] = useState<'' | 'FULL_TIME' | 'PART_TIME'>('');
  const [businessTitle, setBusinessTitle] = useState('');
  const [businessTitleMode, setBusinessTitleMode] = useState<'KEEP' | 'CHANGE' | 'CLEAR'>('KEEP');
  const [reason, setReason] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [createdDraft, setCreatedDraft] = useState<DraftReference>();
  const [createAttempted, setCreateAttempted] = useState(false);
  const [requestKeys, setRequestKeys] = useState(() => ({ create: crypto.randomUUID(), submit: crypto.randomUUID() }));

  const selectedWorkerType = selectedWorker?.workerTypeId === 'CONTINGENT_WORKER' ? 'CONTINGENT_WORKER' : 'EMPLOYEE';
  const currentLocationID = selectedWorker?.locationId ||
    (fixtureMode ? fixtureLocations.find((location) => location.descriptor === selectedWorker?.location)?.id : '') || '';
  const effectiveLocationID = locationID || currentLocationID;
  const jobTypesResource = useAsyncResource(
    () => !dialogOpen || changeType !== 'JOB_CHANGE' || !effectiveDate || !selectedWorker
      ? Promise.resolve([] as StaffingHireReference[])
      : fixtureMode
        ? Promise.resolve(fixtureJobTypes[selectedWorkerType])
        : port.listJobTypes(selectedWorkerType, effectiveDate),
    [changeType, port, dialogOpen, effectiveDate, fixtureMode, selectedWorker?.id, selectedWorkerType],
  );
  const workSpacesResource = useAsyncResource(
    () => !dialogOpen || changeType !== 'JOB_CHANGE' || !effectiveDate || !selectedWorker || !effectiveLocationID
      ? Promise.resolve([] as StaffingHireReference[])
      : fixtureMode
        ? Promise.resolve(fixtureWorkSpaces[effectiveLocationID] ?? [])
        : port.listWorkSpaces(effectiveLocationID, effectiveDate),
    [changeType, port, dialogOpen, effectiveDate, effectiveLocationID, fixtureMode, selectedWorker?.id],
  );

  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionDate, setCorrectionDate] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionError, setCorrectionError] = useState('');
  const [correctionKeys, setCorrectionKeys] = useState(() => ({ create: crypto.randomUUID(), submit: crypto.randomUUID() }));

  const openWorkflowRequest = (workflow: StaffingReference) => {
    if (!canOpenWorkflowRequests) return;
    navigation.openWorkflowRequest(workflow);
  };

  const recentChanges = history.data?.data ?? [];
  const allChanges = useMemo(() => {
    const changesByID = new Map<string, StaffingChangeSummary>();
    for (const change of [...(draftHistory.data?.data ?? []), ...(approvalHistory.data?.data ?? []), ...(scheduledHistory.data?.data ?? []), ...(completedHistory.data?.data ?? []), ...recentChanges]) {
      changesByID.set(change.id, change);
    }
    return [...changesByID.values()];
  }, [approvalHistory.data?.data, completedHistory.data?.data, draftHistory.data?.data, recentChanges, scheduledHistory.data?.data]);
  const historyTotal = history.data?.total ?? recentChanges.length;
  const archiveTruncated = historyTotal > recentChanges.length;
  const activeHistoryTruncated = [draftHistory.data, approvalHistory.data, scheduledHistory.data]
    .some((resource) => resource && resource.total > resource.data.length);
  const historyLoading = history.loading || draftHistory.loading || approvalHistory.loading || scheduledHistory.loading || completedHistory.loading;
  const historyError = history.error || draftHistory.error || approvalHistory.error || scheduledHistory.error || completedHistory.error;
  const reloadHistory = () => { history.reload(); draftHistory.reload(); approvalHistory.reload(); scheduledHistory.reload(); completedHistory.reload(); };
  useEffect(() => {
    if (!selectedChangeID) return;
    const refreshed = allChanges.find((change) => change.id === selectedChangeID);
    if (refreshed) setSelectedChange(refreshed);
  }, [allChanges, selectedChangeID]);
  const rows = useMemo<StaffingRow[]>(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('ko-KR');
    return allChanges
      .filter((change) => !processFilter || change.processType === processFilter)
      .filter((change) => matchesStatus(change, statusFilter, today))
      .filter((change) => !normalizedQuery || `${change.descriptor} ${subjectLabel(change)} ${processLabel(change.processType)} ${change.changes?.map((item) => `${item.label} ${item.before ?? ''} ${item.after ?? ''}`).join(' ') ?? ''}`.toLocaleLowerCase('ko-KR').includes(normalizedQuery))
      .sort((left, right) => (right.effectiveDate ?? '').localeCompare(left.effectiveDate ?? '') || right.createdAt.localeCompare(left.createdAt))
      .map((change) => {
        const matchedWorker = fixtureMode ? fixtureStaffingWorkers.find((worker) => worker.id === change.worker?.id || worker.personId === change.person?.id || worker.name === subjectLabel(change)) : undefined;
        return {
          id: change.id,
          worker: subjectLabel(change),
          workerNumber: matchedWorker?.workerNumber ?? '',
          process: processLabel(change.processType),
          summary: change.changes?.map((item) => item.label).join(' · ') || change.descriptor,
          effectiveDate: formatDateOnly(change.effectiveDate),
          activityDate: formatInstantDate(change.submittedAt || change.createdAt),
          activityLabel: change.submittedAt ? '요청' : '작성',
          status: change.status,
          source: change,
        };
      });
  }, [allChanges, fixtureMode, processFilter, query, statusFilter, today]);

  const selectedOrganization = organizationsResource.data?.find((item) => item.id === organizationID);
  const selectedJob = jobsResource.data?.find((item) => item.id === jobProfileID);
  const selectedJobLevel = jobLevelsResource.data?.find((item) => item.id === jobLevelID);
  const selectedJobType = jobTypesResource.data?.find((item) => item.id === jobTypeID);
  const selectedLocation = locationsResource.data?.find((item) => item.id === locationID);
  const selectedWorkSpace = workSpacesResource.data?.find((item) => item.id === workSpaceSelection);
  const intentFrozen = createAttempted || Boolean(createdDraft);
  const previewChanges = useMemo(() => {
    if (!selectedWorker) return [];
    if (changeType !== 'JOB_CHANGE') return [{ field: 'status', label: '재직 상태', before: selectedWorker.status, after: lifecycleAfterLabel(changeType) }];
    const currentBusinessTitle = selectedWorker.businessTitle?.trim() || '';
    const proposedBusinessTitle = businessTitleMode === 'KEEP' ? currentBusinessTitle : businessTitleMode === 'CLEAR' ? '' : businessTitle.trim() || currentBusinessTitle;
    return [
      ...(selectedOrganization && (selectedWorker.organizationId ? selectedOrganization.id !== selectedWorker.organizationId : selectedOrganization.name !== selectedWorker.organization)
        ? [{ field: 'organization', label: '소속 조직', before: selectedWorker.organization, after: selectedOrganization.name }] : []),
      ...(selectedJob && selectedJob.descriptor !== (selectedWorker.jobProfile || selectedWorker.title)
        ? [{ field: 'jobProfile', label: '직무', before: selectedWorker.jobProfile || selectedWorker.title, after: selectedJob.descriptor }] : []),
      ...(selectedJobLevel && selectedJobLevel.descriptor !== selectedWorker.jobLevel
        ? [{ field: 'jobLevel', label: '직무 레벨', before: selectedWorker.jobLevel || '-', after: selectedJobLevel.descriptor }] : []),
      ...(selectedJobType && (selectedWorker.jobTypeId ? selectedJobType.id !== selectedWorker.jobTypeId : selectedJobType.descriptor !== selectedWorker.jobType)
        ? [{ field: 'jobType', label: selectedWorkerType === 'EMPLOYEE' ? '고용 유형' : '외부 인력 유형', before: selectedWorker.jobType || '-', after: selectedJobType.descriptor }] : []),
      ...(selectedLocation && (selectedWorker.locationId ? selectedLocation.id !== selectedWorker.locationId : selectedLocation.descriptor !== selectedWorker.location)
        ? [{ field: 'location', label: '근무지', before: selectedWorker.location, after: selectedLocation.descriptor }] : []),
      ...(workSpaceSelection === clearWorkSpaceValue && selectedWorker.workSpace
        ? [{ field: 'workSpace', label: '업무 공간', before: selectedWorker.workSpace, after: '-' }]
        : selectedWorkSpace && (selectedWorker.workSpaceId ? selectedWorkSpace.id !== selectedWorker.workSpaceId : selectedWorkSpace.descriptor !== selectedWorker.workSpace)
          ? [{ field: 'workSpace', label: '업무 공간', before: selectedWorker.workSpace || '-', after: selectedWorkSpace.descriptor }]
          : []),
      ...(timeType && timeType !== selectedWorker.timeType
        ? [{ field: 'timeType', label: '근무 형태', before: selectedWorker.timeType ? timeTypeLabels[selectedWorker.timeType] : '-', after: timeTypeLabels[timeType] }] : []),
      ...(proposedBusinessTitle !== currentBusinessTitle
        ? [{ field: 'businessTitle', label: '직함', before: currentBusinessTitle || '-', after: proposedBusinessTitle || '-' }] : []),
    ];
  }, [businessTitle, businessTitleMode, changeType, selectedJob, selectedJobLevel, selectedJobType, selectedLocation, selectedOrganization, selectedWorkSpace, selectedWorker, selectedWorkerType, timeType, workSpaceSelection]);

  const resetComposer = () => {
    setDialogStep(0);
    setChangeType('JOB_CHANGE');
    setSelectedWorker(undefined);
    setWorkerSearch('');
    setWorkerSearchQuery('');
    setEffectiveDate('');
    setOrganizationID('');
    setJobProfileID('');
    setJobLevelID('');
    setJobTypeID('');
    setLocationID('');
    setWorkSpaceSelection('');
    setTimeType('');
    setBusinessTitle('');
    setBusinessTitleMode('KEEP');
    setReason('');
    setSubmitError('');
    setCreatedDraft(undefined);
    setCreateAttempted(false);
    setRequestKeys({ create: crypto.randomUUID(), submit: crypto.randomUUID() });
  };

  const openComposer = () => {
    resetComposer();
    setNotice('');
    setNoticeWorkflow(undefined);
    setDialogOpen(true);
  };

  const closeComposer = () => {
    if (submitLoading) return;
    setDialogOpen(false);
    resetComposer();
  };

  const moveToDetails = () => {
    if (!effectiveDate || !selectedWorker) {
      setSubmitError('대상 구성원과 시행일을 입력해 주세요.');
      return;
    }
    setSubmitError('');
    setDialogStep(1);
  };

  const fixtureDraft = (): StaffingChangeSummary => ({
    id: `fixture-${crypto.randomUUID()}`,
    descriptor: `${selectedWorker?.name ?? ''} ${processLabel(changeType)}`,
    processType: changeType,
    subject: { id: selectedWorker?.id ?? '', descriptor: selectedWorker?.name },
    worker: { id: selectedWorker?.id ?? '', descriptor: selectedWorker?.name },
    effectiveDate,
    status: 'DRAFT',
    createdAt: new Date().toISOString(),
    changes: previewChanges,
  });

  const createDraft = async (): Promise<DraftReference> => {
    if (fixtureMode) {
      const draft = fixtureDraft();
      setFixtureChanges((current) => [draft, ...current]);
      return { id: draft.id, processType: draft.processType };
    }
    if (changeType === 'JOB_CHANGE') {
      const currentBusinessTitle = selectedWorker!.businessTitle?.trim() || '';
      const proposedBusinessTitle = businessTitleMode === 'KEEP' ? currentBusinessTitle : businessTitleMode === 'CLEAR' ? '' : businessTitle.trim() || currentBusinessTitle;
      const draft = await port.createJobChange(selectedWorker!.id, {
        effectiveDate,
        reason: reason.trim() || undefined,
        businessTitle: proposedBusinessTitle && proposedBusinessTitle !== currentBusinessTitle ? proposedBusinessTitle : undefined,
        clearBusinessTitle: Boolean(currentBusinessTitle && !proposedBusinessTitle) || undefined,
        supervisoryOrganization: selectedOrganization ? { id: selectedOrganization.id, descriptor: selectedOrganization.name } : undefined,
        jobProfile: selectedJob ? { id: selectedJob.id, descriptor: selectedJob.descriptor } : undefined,
        jobLevel: selectedJobLevel ? { id: selectedJobLevel.id, descriptor: selectedJobLevel.descriptor } : undefined,
        jobType: selectedJobType ? { id: selectedJobType.id, descriptor: selectedJobType.descriptor } : undefined,
        location: selectedLocation ? { id: selectedLocation.id, descriptor: selectedLocation.descriptor } : undefined,
        workSpace: selectedWorkSpace ? { id: selectedWorkSpace.id, descriptor: selectedWorkSpace.descriptor } : undefined,
        clearWorkSpace: workSpaceSelection === clearWorkSpaceValue || undefined,
        timeType: timeType || undefined,
      }, requestKeys.create);
      return { id: draft.id, processType: 'JOB_CHANGE', workflow: draft.workflow };
    }
    const draft = await port.createWorkerLifecycle(selectedWorker!.id, changeType, { effectiveDate, reason: reason.trim() || undefined }, requestKeys.create);
    return { id: draft.id, processType: changeType, workflow: draft.workflow };
  };

  const submitDraft = async (draft: DraftReference): Promise<Pick<StaffingDraft, 'status' | 'workflow'>> => {
    if (fixtureMode) {
      setFixtureChanges((current) => current.map((change) => change.id === draft.id ? { ...change, status: 'IN_PROGRESS', submittedAt: new Date().toISOString() } : change));
      return { status: 'IN_PROGRESS', workflow: draft.workflow };
    }
    const submitted = draft.processType === 'JOB_CHANGE'
      ? await port.submitJobChange(draft.id, requestKeys.submit)
      : await port.submitWorkerLifecycle(draft.id, requestKeys.submit);
    return { status: submitted.status, workflow: submitted.workflow ?? draft.workflow };
  };

  const persistComposer = async (action: 'SAVE' | 'SUBMIT') => {
    if (!canMutate || !selectedWorker || !effectiveDate) return;
    if (changeType === 'JOB_CHANGE' && businessTitleMode === 'CHANGE' && !businessTitle.trim()) { setSubmitError('변경할 직함을 입력하거나 직함 해제를 선택해 주세요.'); return; }
    if (changeType === 'JOB_CHANGE' && previewChanges.length === 0) {
      setSubmitError('변경할 조직, 직무, 직무 레벨, 고용 유형, 근무지, 업무 공간, 근무 형태 또는 직함을 하나 이상 입력해 주세요.');
      return;
    }
    setSubmitLoading(true);
    setSubmitError('');
    let draft = createdDraft;
    try {
      if (!draft) {
        setCreateAttempted(true);
        draft = await createDraft();
        setCreatedDraft(draft);
      }
      const submitted = action === 'SUBMIT' ? await submitDraft(draft) : undefined;
      setDialogOpen(false);
      setNotice(action === 'SAVE'
        ? `${selectedWorker.name}님의 ${processLabel(changeType)}을 초안으로 저장했습니다.`
        : `${selectedWorker.name}님의 ${processLabel(changeType)}을 ${submitted?.status === 'COMPLETED' ? '확정했습니다.' : '승인 요청했습니다.'}`);
      setNoticeWorkflow(submitted?.workflow ?? draft.workflow);
      resetComposer();
      reloadHistory();
    } catch (failure) {
      reloadHistory();
      const status = errorStatus(failure);
      if (!draft && status !== undefined && status >= 400 && status < 500) setCreateAttempted(false);
      setSubmitError(failure instanceof Error ? failure.message : action === 'SAVE' ? '발령 초안을 저장하지 못했습니다.' : '발령을 승인 요청하지 못했습니다.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const submitSelectedDraft = async () => {
    if (!canMutate || !selectedChange || selectedChange.id !== selectedChangeID || selectedChange.status !== 'DRAFT' || detailActionLoading) return;
    const change = selectedChange;
    let idempotencyKey = detailSubmitKeys.current.get(change.id);
    if (!idempotencyKey) {
      idempotencyKey = crypto.randomUUID();
      detailSubmitKeys.current.set(change.id, idempotencyKey);
    }
    setDetailActionStates((current) => ({ ...current, [change.id]: { loading: true } }));
    try {
      let submittedStatus = 'IN_PROGRESS';
      if (fixtureMode) {
        const submittedAt = new Date().toISOString();
        setFixtureChanges((current) => current.map((item) => item.id === change.id ? { ...item, status: 'IN_PROGRESS', submittedAt } : item));
        setNoticeWorkflow(change.workflow);
      } else if (change.processType === 'JOB_CHANGE') {
        const submitted = await port.submitJobChange(change.id, idempotencyKey);
        submittedStatus = submitted.status ?? 'IN_PROGRESS';
        setNoticeWorkflow(submitted.workflow ?? change.workflow);
      } else {
        const submitted = await port.submitWorkerLifecycle(change.id, idempotencyKey);
        submittedStatus = submitted.status ?? 'IN_PROGRESS';
        setNoticeWorkflow(submitted.workflow ?? change.workflow);
      }
      const submittedAt = new Date().toISOString();
      setSelectedChange((current) => current?.id === change.id ? { ...current, status: submittedStatus, submittedAt } : current);
      detailSubmitKeys.current.delete(change.id);
      setNotice(`${subjectLabel(change)}님의 ${processLabel(change.processType)}을 ${submittedStatus === 'COMPLETED' ? '확정했습니다.' : '승인 요청했습니다.'}`);
      reloadHistory();
    } catch (failure) {
      reloadHistory();
      const error = failure instanceof Error ? `${failure.message} 목록에서 현재 상태를 다시 확인했습니다.` : '승인 요청 결과를 확인하지 못해 목록에서 현재 상태를 다시 확인했습니다.';
      setDetailActionStates((current) => ({ ...current, [change.id]: { loading: false, error } }));
    } finally {
      setDetailActionStates((current) => ({ ...current, [change.id]: { ...current[change.id], loading: false } }));
    }
  };

  const openCorrection = () => {
    if (!canMutate || !selectedChange) return;
    setCorrectionDate(selectedChange.effectiveDate || '');
    setCorrectionReason('');
    setCorrectionError('');
    setCorrectionKeys({ create: crypto.randomUUID(), submit: crypto.randomUUID() });
    closeDetail();
    setCorrectionOpen(true);
  };

  const submitCorrection = async () => {
    if (!canMutate || !selectedChange) return;
    if (!correctionDate || !correctionReason.trim()) {
      setCorrectionError('시행일과 정정 사유를 입력해 주세요.');
      return;
    }
    if (fixtureMode) {
      setCorrectionOpen(false);
      setNotice(`${subjectLabel(selectedChange)}님의 시행일 정정을 승인 요청했습니다.`);
      setNoticeWorkflow(undefined);
      return;
    }
    setSubmitLoading(true);
    setCorrectionError('');
    try {
      const draft = await port.correctWorkerLifecycle(selectedChange.id, { effectiveDate: correctionDate, reason: correctionReason.trim() }, correctionKeys.create);
      const submitted = await port.submitWorkerLifecycle(draft.id, correctionKeys.submit);
      setCorrectionOpen(false);
      setNotice(`${subjectLabel(selectedChange)}님의 시행일 정정을 승인 요청했습니다.`);
      setNoticeWorkflow(submitted.workflow ?? draft.workflow);
      reloadHistory();
    } catch (failure) {
      if (errorStatus(failure) === 409 && errorCode(failure) === 'REVIEW_REQUIRED') {
        setCorrectionError('후속 완료 발령이 있어 자동 정정할 수 없습니다. 영향 범위를 검토한 뒤 새 발령으로 처리해 주세요.');
      } else {
        setCorrectionError(failure instanceof Error ? failure.message : '발령을 정정하지 못했습니다.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const managedPersonnel = selectedChange && isManagedPersonnelRequest(selectedChange);
  const domainDetailAction = selectedChange && managedPersonnel && ['DRAFT', 'DENIED', 'IN_PROGRESS'].includes(selectedChange.status)
    ? <WorkerPersonnelRequestActions key={selectedChange.id} target={selectedChange} port={port} fixtureMode={fixtureMode} canEdit={canMutate} canCancel={Boolean(access.canCancelLifecycle)} disabled={detailActionLoading} onBusyChange={setPersonnelBusy} onChanged={reloadHistory} onOpenWorkflow={canOpenWorkflowRequests ? openWorkflowRequest : undefined} />
    : selectedChange && canMutate && selectedChange.status === 'DRAFT'
    ? <Stack spacing={1}>{detailActionError && <Alert severity="error">{detailActionError}</Alert>}<Button fullWidth variant="contained" disabled={detailActionLoading} onClick={() => void submitSelectedDraft()}>{detailActionLoading ? '요청 중…' : '승인 요청'}</Button></Stack>
    : selectedChange && canMutate && selectedChange.status === 'COMPLETED' && selectedChange.processType !== 'JOB_CHANGE'
      ? <Button fullWidth variant="outlined" onClick={openCorrection}>시행일 정정</Button>
      : undefined;
  const detailActions = selectedChange && (domainDetailAction || (selectedChange.workflow && canOpenWorkflowRequests))
    ? <Stack spacing={1}>
        {domainDetailAction}
        {selectedChange.workflow && canOpenWorkflowRequests && <Button fullWidth variant="outlined" onClick={() => openWorkflowRequest(selectedChange.workflow!)}>결재 문서 열기</Button>}
      </Stack>
    : undefined;
  const collectionState: ResourceCollectionState = historyLoading
    ? { kind: 'loading' }
    : historyError
      ? { kind: 'error', message: historyError.message, onRetry: reloadHistory }
      : rows.length === 0
        ? {
            kind: 'empty',
            title: allChanges.length ? '검색 조건에 맞는 인사 발령이 없습니다.' : '아직 인사 발령이 없습니다.',
            description: allChanges.length
              ? '검색어나 필터를 변경해 주세요.'
              : canMutate ? '개별 발령에서 첫 발령을 초안으로 준비해 보세요.' : '발령이 등록되면 이곳에서 확인할 수 있습니다.',
            action: query || processFilter || statusFilter ? <Button variant="weak" onClick={() => { setQuery(''); setProcessFilter(''); setStatusFilter(''); }}>검색 조건 초기화</Button> : undefined,
          }
        : { kind: 'ready' };

  return <>
    <ResourcePage
      title="인사 발령"
      primaryAction={canMutate ? <Button variant="fill" startIcon={<AddOutlined />} onClick={() => { setView('plans'); setComposePlanRequest((value) => value + 1); }}>발령안 작성</Button> : undefined}
      actions={<>
        {canMutate && <Button variant="weak" onClick={() => { setView('history'); openComposer(); }}>개별 발령</Button>}
        {canMutate && canBulkCreate && <Button variant="weak" startIcon={<UploadFileOutlined />} onClick={navigation.openBulkChange}>대량 발령</Button>}
        {navigation.openBulkHistory && <Button variant="weak" onClick={navigation.openBulkHistory}>처리 내역</Button>}
      </>}
      tabs={<FilterTabs value={view} onChange={setView} ariaLabel="인사 발령 보기" items={[{ value: 'plans', label: '발령안', controls: 'staffing-plans' }, { value: 'history', label: '개별 발령 이력', controls: 'staffing-history' }]} />}
    >
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Box id="staffing-plans" hidden={view !== 'plans'} sx={{ flex: 1, minHeight: 0, display: view === 'plans' ? 'flex' : 'none', flexDirection: 'column' }}>
          <StaffingPlansWorkspace port={port} access={access} navigation={navigation} detailRoute={planDetailRoute} fixtureMode={fixtureMode} composeRequest={composePlanRequest} active={view === 'plans'} />
        </Box>
      <Box id="staffing-history" hidden={view !== 'history'} sx={{ flex: 1, minHeight: 0, display: view === 'history' ? 'flex' : 'none', flexDirection: 'column' }}>
        {(activeHistoryTruncated || archiveTruncated) && <Stack spacing={1.5} sx={{ mb: 1.5, flexShrink: 0 }}>
          {(activeHistoryTruncated || archiveTruncated) && <Alert severity="info">
            {activeHistoryTruncated
              ? '빠른 조회를 위해 상태별 최신 100건을 표시합니다. 전체 건수는 목록 전체 건수에 반영됩니다.'
              : `종료 이력은 최신 ${recentChanges.length}건을 표시합니다.`}
          </Alert>}
        </Stack>}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {view === 'history' && <StaffingChangesTable
            state={collectionState}
            rows={rows}
            totalCount={historyTotal}
            compact={compactTable}
            today={today}
            query={query}
            onQueryChange={setQuery}
            processFilter={processFilter}
            statusFilter={statusFilter}
            onProcessFilterChange={setProcessFilter}
            onStatusFilterChange={setStatusFilter}
            onRowClick={(change) => {
              setSelectedChange(change);
              openDetail(change.id);
            }}
          />}
        </Box>
      </Box>
      </Stack>
    </ResourcePage>
    <FeedbackSnackbar
      open={Boolean(notice)}
      message={notice}
      tone="success"
      onClose={() => { setNotice(''); setNoticeWorkflow(undefined); }}
      action={noticeWorkflow && canOpenWorkflowRequests ? <Button color="inherit" size="small" onClick={() => openWorkflowRequest(noticeWorkflow)}>결재 문서 열기</Button> : undefined}
    />

    <StaffingChangeComposerDialog
      open={canMutate && dialogOpen}
      values={{
        step: dialogStep,
        changeType,
        effectiveDate,
        workerSearch,
        selectedWorker,
        organizationID,
        jobProfileID,
        jobLevelID,
        jobTypeID,
        locationID,
        workSpaceSelection,
        timeType,
        businessTitle,
        businessTitleMode,
        reason,
        submitLoading,
        submitError,
        createdDraft,
        createAttempted,
        intentFrozen,
        selectedWorkerType,
        currentLocationID,
        effectiveLocationID,
        previewChanges,
      }}
      resources={{
        workers: composerWorkersResource,
        organizations: organizationsResource,
        locations: locationsResource,
        jobs: jobsResource,
        jobLevels: jobLevelsResource,
        jobTypes: jobTypesResource,
        workSpaces: workSpacesResource,
      }}
      actions={{
        close: closeComposer,
        changeType: (value) => {
          setChangeType(value);
          setOrganizationID('');
          setJobProfileID('');
          setJobLevelID('');
          setJobTypeID('');
          setLocationID('');
          setWorkSpaceSelection('');
          setTimeType('');
          setBusinessTitle(value === 'JOB_CHANGE' ? selectedWorker?.businessTitle ?? '' : '');
          setBusinessTitleMode('KEEP');
          setSubmitError('');
        },
        changeEffectiveDate: (value) => {
          setEffectiveDate(value);
          setSelectedWorker(undefined);
          setWorkerSearch('');
          setOrganizationID('');
          setJobProfileID('');
          setJobLevelID('');
          setJobTypeID('');
          setLocationID('');
          setWorkSpaceSelection('');
          setTimeType('');
          setBusinessTitle('');
          setBusinessTitleMode('KEEP');
          setSubmitError('');
        },
        changeWorkerSearch: setWorkerSearch,
        selectWorker: (worker) => {
          setSelectedWorker(worker);
          setOrganizationID('');
          setJobProfileID('');
          setJobLevelID('');
          setJobTypeID('');
          setLocationID('');
          setWorkSpaceSelection('');
          setTimeType('');
          setBusinessTitle(worker?.businessTitle ?? '');
          setBusinessTitleMode('KEEP');
          setSubmitError('');
        },
        changeOrganization: setOrganizationID,
        changeJobProfile: setJobProfileID,
        changeJobLevel: setJobLevelID,
        changeJobType: setJobTypeID,
        changeLocation: setLocationID,
        changeWorkSpace: setWorkSpaceSelection,
        changeTimeType: setTimeType,
        changeBusinessTitle: (value) => { setBusinessTitle(value); setBusinessTitleMode('CHANGE'); },
        changeBusinessTitleMode: (mode) => { setBusinessTitleMode(mode); if (mode === 'KEEP') setBusinessTitle(selectedWorker?.businessTitle ?? ''); if (mode === 'CLEAR') setBusinessTitle(''); },
        changeReason: (value) => { setReason(value); setSubmitError(''); },
        reloadJobChangeReferences: () => {
          organizationsResource.reload();
          jobsResource.reload();
          jobLevelsResource.reload();
          jobTypesResource.reload();
          locationsResource.reload();
          workSpacesResource.reload();
        },
        moveToDetails,
        moveToBasics: () => { setSubmitError(''); setDialogStep(0); },
        persist: (action) => { void persistComposer(action); },
      }}
    />

    {selectedChange && <StaffingChangeDetailDrawer
      open={selectedChangeID === selectedChange.id && !correctionOpen}
      change={selectedChange}
      today={today}
      onClose={() => { if (!detailActionLoading && !personnelBusy) closeDetail(); }}
      footer={detailActions}
    />}
    {selectedChangeID && selectedChange?.id !== selectedChangeID && <DetailDrawer open onClose={closeDetail} title="인사 발령 상세">
      <DetailDrawerBody><ResourceState embedded density="compact" loading={historyLoading} error={historyError?.message} onRetry={reloadHistory} empty emptyTitle="발령 정보를 찾을 수 없습니다." emptyDescription="현재 조회 범위에 없거나 조회할 수 없는 발령입니다." emptyAction={<Button onClick={closeDetail}>목록으로 돌아가기</Button>}>{null}</ResourceState></DetailDrawerBody>
    </DetailDrawer>}

    <StaffingCorrectionDialog
      open={canMutate && correctionOpen}
      busy={submitLoading}
      error={correctionError}
      effectiveDate={correctionDate}
      reason={correctionReason}
      onClose={() => setCorrectionOpen(false)}
      onSubmit={submitCorrection}
      onEffectiveDateChange={setCorrectionDate}
      onReasonChange={setCorrectionReason}
    />
  </>;
}
